<template>
    <div
        :id="id"
        class="graph-block"
        :data-index-path="indexPath"
    >
        <GraphCanvas
            :flow-id="flowId"
            :children="drawn"
            :node-kinds="nodeKinds"
            :edge-kinds="edgeKinds"
            :editable="editable"
            :block-id="id"
            @graph:update="graph => emit('graph:update', graph)"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, inject, type ComputedRef } from 'vue'
import type {
    ExtendedBlock,
    GraphApi,
    GraphEdgeKind,
    GraphNodeKind,
} from 'widget-components/integrations/CustomPage/types'

defineOptions({ inheritAttrs: false })

// `children` are the nodes and edges drawn on the graph, and what may be drawn arrives separately
// as the palette. The authored kinds never reach the canvas: the builder renders those itself.
const props = defineProps<{
    id?: string
    children: ExtendedBlock[]
    indexPath?: number[]
    fieldName?: string
    nodeKinds?: GraphNodeKind[] | null
    edgeKinds?: GraphEdgeKind[] | null
    readonly?: boolean
}>()

// the whole block as the mutation returned it; `Block.vue` writes it back into the page tree
const emit = defineEmits<{
    (e: 'graph:update', graph: GraphApi): void
}>()

const GraphCanvas = defineAsyncComponent(() => import('./GraphCanvas.vue'))

// a preview draws the builder's draft, whose graph carries the authored kinds where a running page
// carries drawn nodes — there is nothing to draw, and no instance to draw it against
const customPagePreview = inject<ComputedRef<boolean>>('customPagePreview', computed(() => false))

const editable = computed(() => !customPagePreview.value && !props.readonly)

const drawn = computed(() => customPagePreview.value ? [] : props.children)

const flowId = computed(() => `graph-${props.id || props.fieldName || 'block'}`)
</script>

<style lang="sass" scoped>
@import "./Graph.sass"
</style>